// AI-CONTEXT: See docs/patterns.md#data-analytics-pin-snapshot

/**
 * @typedef {object} AnalyticsCardState
 * @property {string} [selectedPersonaId]
 * @property {string} [selectedTargetgroupId]
 * @property {string} [selectedLocationId]
 * @property {{ [indicatorId: string]: { SelectedPersonaId?: string } }} [filterByIndicator]
 * @property {'bar' | 'line' | 'pie'} [graphType]
 * @property {{ [key: string]: string }} [colorOverrides]
 * @property {{ [indicatorId: string]: 'bar' | 'line' | 'pie' }} [graphTypeByIndicator]
 * @property {{ [indicatorId: string]: { [key: string]: string } }} [colorOverridesByIndicator]
 * @property {{ [outputId: string]: { [key: string]: string } }} [colorOverridesByOutput]
 * @property {{ [outputId: string]: ('line'|'bar') }} [graphTypeByOutput]
 * @property {{ [fieldId: string]: { [key: string]: string } }} [colorOverridesByEventField]
 * @property {{ start: string|null, end: string|null }} [period] - Primary compare period
 * @property {{ enabled: boolean, start: string|null, end: string|null }} [compare] - Compare period
 */

export const DASHBOARD_FILTER_FIELDS = Object.freeze({
    SelectedPersonaId: 'SelectedPersonaId',
    SelectedTargetgroupId: 'SelectedTargetgroupId',
    SelectedLocationId: 'SelectedLocationId',
    SelectedRouteId: 'SelectedRouteId',
    // Welke deelnemersstatus de aanwezigheidskaart toont: '' = alleen actief,
    // 'all' = iedereen, anders één status uit PERSONA_STATUS_KEYS.
    SelectedPersonaStatus: 'SelectedPersonaStatus',
    FilterByIndicator: 'FilterByIndicator',
    // Compare-two-periods (docs/features/compare-two-periods.md):
    // Period = primary range { start, end }; ComparePeriod = { enabled, start, end }.
    Period: 'Period',
    ComparePeriod: 'ComparePeriod',
})

const hasContent = (value) => {
    if (value === null || value === undefined || value === '') return false
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'object') return Object.keys(value).length > 0
    return true
}

const pruneIndicatorFilters = (filterByIndicator) => {
    const pruned = {}
    for (const [indicatorId, filters] of Object.entries(filterByIndicator || {})) {
        const innerKept = {}
        for (const [field, value] of Object.entries(filters || {})) {
            if (hasContent(value)) innerKept[field] = value
        }
        if (Object.keys(innerKept).length > 0) pruned[indicatorId] = innerKept
    }
    return pruned
}

/**
 * Take an analytics card's current React state and return the field set ready
 * to spread into a `DashboardItems` write at pin time. Empty / default values
 * are omitted so the persisted doc stays clean.
 *
 * @param {AnalyticsCardState} state
 * @returns {object}
 */
const snapshotAnalyticsState = (state = {}) => {
    const snapshot = {}

    if (hasContent(state.selectedPersonaId)) {
        snapshot[DASHBOARD_FILTER_FIELDS.SelectedPersonaId] = state.selectedPersonaId
    }
    if (hasContent(state.selectedTargetgroupId)) {
        snapshot[DASHBOARD_FILTER_FIELDS.SelectedTargetgroupId] = state.selectedTargetgroupId
    }
    if (hasContent(state.selectedLocationId)) {
        snapshot[DASHBOARD_FILTER_FIELDS.SelectedLocationId] = state.selectedLocationId
    }

    if (state.filterByIndicator) {
        const pruned = pruneIndicatorFilters(state.filterByIndicator)
        if (Object.keys(pruned).length > 0) {
            snapshot[DASHBOARD_FILTER_FIELDS.FilterByIndicator] = pruned
        }
    }

    if (hasContent(state.graphType)) {
        snapshot.GraphType = state.graphType
    }
    if (hasContent(state.colorOverrides)) {
        snapshot.ColorOverrides = state.colorOverrides
    }
    if (hasContent(state.graphTypeByIndicator)) {
        snapshot.GraphTypeByIndicator = state.graphTypeByIndicator
    }
    if (hasContent(state.colorOverridesByIndicator)) {
        snapshot.ColorOverridesByIndicator = state.colorOverridesByIndicator
    }
    if (hasContent(state.colorOverridesByOutput)) {
        snapshot.ColorOverridesByOutput = state.colorOverridesByOutput
    }
    if (hasContent(state.graphTypeByOutput)) {
        snapshot.GraphTypeByOutput = state.graphTypeByOutput
    }
    if (hasContent(state.colorOverridesByEventField)) {
        snapshot.ColorOverridesByEventField = state.colorOverridesByEventField
    }

    // Compare-two-periods: only persist a period when it actually filters
    // something (a bound set), and a compare period only when enabled.
    if (state.period && (state.period.start || state.period.end)) {
        snapshot[DASHBOARD_FILTER_FIELDS.Period] = {
            start: state.period.start || null,
            end: state.period.end || null,
        }
    }
    if (state.compare && state.compare.enabled) {
        snapshot[DASHBOARD_FILTER_FIELDS.ComparePeriod] = {
            enabled: true,
            start: state.compare.start || null,
            end: state.compare.end || null,
        }
    }

    return snapshot
}

export default snapshotAnalyticsState
